/** Staff billing: invoices for a client, payments, and holding documents until paid. */
import {
  ADJUSTMENT_LABELS,
  ADJUSTMENT_TYPES,
  formatCents,
  MODULE_LABELS,
  MODULES,
  parseDollars,
  REFUND_METHOD_LABELS,
  clientEventLabel,
  type AdjustmentType,
  type ClientInvoiceEvent,
  type InvoiceEventKind,
  type Module,
  type RefundMethod,
} from "@clientflow/shared";
import { ask } from "@/components/dialogs";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Empty, Loading, LoadingBox, Pager, Section, useServerPaged, type Paged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/lib/use-toast";
import { api, errorMessage } from "@/lib/api";
import { daysBetween, formatDate, localToday } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import type { Engagement } from "@/lib/types";

export type InvoiceSummary = {
  id: string;
  number: number | null;
  clientId: string;
  clientName?: string;
  engagementId: string | null;
  status: "draft" | "open" | "paid" | "void";
  totalCents: number;
  amountPaidCents: number;
  /** What's still owed and what's been paid beyond it (from the server's shared balance). */
  balanceCents?: number;
  overpaidCents?: number;
  /** Credit the firm holds for the invoice's client. */
  clientCreditCents?: number;
  issuedOn: string | null;
  dueOn: string | null;
  // Fees taken from the client's refund, which the client approves in the portal.
  paidFromRefund?: boolean;
  approvalRequestedAt?: string | null;
  clientApprovedAt?: string | null;
  approvalDeclinedAt?: string | null;
  approvalDeclineNote?: string | null;
};

/** A small status pill beside a client's name in the invoice list; its text wraps on a phone rather than being cut. */
const BADGE = "rounded-full px-2 py-0.5 text-xs font-medium sm:whitespace-nowrap";

/** Where a fee taken from the refund stands with the client. */
function RefundFeeBadge({ inv }: { inv: InvoiceSummary }) {
  if (!inv.paidFromRefund) return null;
  const [label, cls] = inv.clientApprovedAt
    ? ["From the refund · approved", "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200"]
    : inv.approvalDeclinedAt
      ? ["Client asked for a change", "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200"]
      : inv.approvalRequestedAt
        ? ["Waiting for client approval", "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"]
        : ["From the refund", "bg-secondary text-muted-foreground"];
  return <span className={`${BADGE} ${cls}`}>{label}</span>;
}

export type InvoiceEvent = {
  id: string;
  kind: InvoiceEventKind;
  amountCents: number;
  method: string | null;
  reason: string;
  showToClient: boolean;
  status: "pending" | "succeeded" | "failed";
  paymentId: string | null;
  relatedInvoiceId: string | null;
  reference: string | null;
  effectiveOn: string;
  createdAt: string;
  byName: string;
};

export type InvoiceDetail = {
  invoice: InvoiceSummary & {
    memo: string | null;
    balanceCents: number;
    overpaidCents: number;
    netPaidCents: number;
    adjustedCents: number;
    refundedCents: number;
    creditedCents: number;
    clientName: string;
    paidAt: string | null;
    autopayStatus?: "processing" | "succeeded" | "failed" | null;
    autopayError?: string | null;
    autopayNextAttemptOn?: string | null;
  };
  clientCreditCents: number;
  lines: Array<{ id: string; description: string; quantity: number; unitPriceCents: number; amountCents: number }>;
  payments: Array<{ id: string; amountCents: number; refundedCents?: number; method: string; reference: string | null; receivedOn: string }>;
  events?: InvoiceEvent[];
  /** Whether the signed-in staff member may refund, credit, adjust and void (staff API only). */
  canAdjust?: boolean;
};

const STATUS: Record<InvoiceSummary["status"] | "past_due", { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "bg-muted text-muted-foreground" },
  open: { label: "Open", cls: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200" },
  past_due: { label: "Past due", cls: "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200" },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" },
  void: { label: "Void", cls: "bg-muted text-muted-foreground line-through" },
};

/** Days an open invoice is past its due date (0 when not yet due or not open). */
export function daysPastDue(inv: Pick<InvoiceSummary, "status" | "dueOn">, today = localToday()): number {
  return inv.status === "open" && inv.dueOn ? Math.max(0, daysBetween(inv.dueOn, today)) : 0;
}

export function InvoiceStatus({ status, dueOn }: { status: InvoiceSummary["status"]; dueOn?: string | null }) {
  const key = daysPastDue({ status, dueOn: dueOn ?? null }) > 0 ? "past_due" : status;
  return <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS[key].cls}`}>{STATUS[key].label}</span>;
}

/** One page of invoices from GET /api/invoices. */
export type InvoicePage = { items: InvoiceSummary[]; total: number };

/** Balance due on open invoices by days past the due date, from GET /api/invoices/aging. */
export type Aging = { openCount: number; balanceCents: number; currentCents: number; days1to30Cents: number; days31to60Cents: number; over60Cents: number; /** One client's ageing only. */ creditCents?: number };

export const METHOD_LABELS: Record<string, string> = {
  stripe: "Online (card/bank)",
  check: "Check",
  cash: "Cash",
  zelle: "Zelle",
  bank_product: "Refund transfer (bank product)",
  credit: "Credit applied",
  other: "Other",
};

type LineDraft = { description: string; quantity: string; price: string };

/** Turns form lines into cents, or explains the first problem. Never uses floating point. */
function priceDraft(lines: LineDraft[]): { lines: Array<{ description: string; quantity: number; unitPriceCents: number }>; totalCents: number } | string {
  const out = [];
  let total = 0;
  for (const [i, l] of lines.entries()) {
    if (!l.description.trim()) return `Line ${i + 1}: enter a description`;
    const q = Number(l.quantity);
    if (!Number.isInteger(q) || q < 1) return `Line ${i + 1}: quantity must be a whole number`;
    let cents: number;
    try {
      cents = parseDollars(l.price);
    } catch {
      return `Line ${i + 1}: enter a price such as 150 or 150.00`;
    }
    if (cents < 0) return `Line ${i + 1}: the price cannot be negative`;
    out.push({ description: l.description.trim(), quantity: q, unitPriceCents: cents });
    total += q * cents;
  }
  return { lines: out, totalCents: total };
}

function NewInvoice({ clientId, onDone }: { clientId: string; onDone: () => void }) {
  const { data: work } = useQuery<{ items: Engagement[] }>({ queryKey: ["/api/engagements?clientId=", clientId] });
  // Finished work is billed too (often the biggest leak when it isn't).
  const { data: finished } = useQuery<{ items: Engagement[] }>({ queryKey: ["/api/engagements?status=completed&limit=100&clientId=", clientId] });
  const engagements = [...(work?.items ?? []), ...(finished?.items ?? [])];
  const [module, setModule] = useState<Module | "">("");
  const [lines, setLines] = useState<LineDraft[]>([{ description: "", quantity: "1", price: "" }]);
  const [engagementId, setEngagementId] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [memo, setMemo] = useState("");
  // Once the draft exists, a retry of "Issue" must not create a second one.
  const [draftId, setDraftId] = useState<string | null>(null);
  const priced = priceDraft(lines);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: async (issue: boolean) => {
      if (typeof priced === "string") throw new Error(priced);
      if (!engagementId && !module) throw new Error("Choose the engagement or the service this invoice is for");
      const body = { engagementId: engagementId || null, module: engagementId ? null : module || null, dueOn: dueOn || null, memo: memo || undefined, lines: priced.lines };
      let id = draftId;
      if (id) await api.send("PUT", `/api/invoices/${id}`, body);
      else {
        id = (await api.send<InvoiceDetail>("POST", "/api/invoices", { clientId, ...body })).invoice.id;
        setDraftId(id);
      }
      if (issue) await api.send("POST", `/api/invoices/${id}/issue`);
    },
    onSuccess: onDone,
  });
  const set = (i: number, k: keyof LineDraft, v: string) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <p className="text-sm font-medium">New invoice</p>
      {lines.map((l, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-[3fr_1fr_1fr_auto]">
          <Input aria-label={`Line ${i + 1} description`} placeholder="e.g. 2026 Form 1040 preparation" value={l.description} onChange={(e) => set(i, "description", e.target.value)} />
          <Input aria-label={`Line ${i + 1} quantity`} inputMode="numeric" value={l.quantity} onChange={(e) => set(i, "quantity", e.target.value)} />
          <Input aria-label={`Line ${i + 1} price`} inputMode="decimal" placeholder="$0.00" value={l.price} onChange={(e) => set(i, "price", e.target.value)} />
          <Button type="button" variant="ghost" aria-label="Remove line" disabled={lines.length === 1} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="ghost" onClick={() => setLines((ls) => [...ls, { description: "", quantity: "1", price: "" }])}>
        <Plus className="mr-1 h-4 w-4" /> Add line
      </Button>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1">
          <Label htmlFor="inv-work">Engagement</Label>
          <select id="inv-work" value={engagementId} onChange={(e) => setEngagementId(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="">None</option>
            {engagements.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
                {e.status === "completed" ? " (complete)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="inv-service">Service</Label>
          {engagementId ? (
            <p className="flex h-9 items-center text-sm text-muted-foreground">{MODULE_LABELS[engagements.find((e) => e.id === engagementId)?.module ?? "tax"]} (from the engagement)</p>
          ) : (
            <select id="inv-service" value={module} onChange={(e) => setModule(e.target.value as Module | "")} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" aria-describedby="inv-service-help">
              <option value="">Choose the service</option>
              {MODULES.map((m) => (
                <option key={m} value={m}>
                  {MODULE_LABELS[m]}
                </option>
              ))}
            </select>
          )}
          <p id="inv-service-help" className="sr-only">Firm Analytics reports revenue by service.</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="inv-due">Due date</Label>
          <Input id="inv-due" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inv-memo">Memo to client</Label>
          <Input id="inv-memo" value={memo} onChange={(e) => setMemo(e.target.value)} />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm">
          Total: <span className="font-semibold">{typeof priced === "string" ? "—" : formatCents(priced.totalCents)}</span>
          {typeof priced === "string" && lines.some((l) => l.price) && <span className="ml-2 text-destructive">{priced}</span>}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" disabled={save.isPending || typeof priced === "string" || (!engagementId && !module)} onClick={() => save.mutate(false)}>
            Save draft
          </Button>
          <Button disabled={save.isPending || typeof priced === "string" || (typeof priced !== "string" && priced.totalCents <= 0) || (!engagementId && !module)} onClick={() => save.mutate(true)}>
            Issue invoice
          </Button>
        </div>
      </div>
      {!engagementId && !module && <p className="text-xs text-muted-foreground">Choose the engagement or the service this invoice is for, so your revenue reports show where it came from.</p>}
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
    </div>
  );
}

/** A history line as staff read it: what happened, how, and why. */
function staffEventLabel(e: InvoiceEvent): string {
  switch (e.kind) {
    case "refund":
      return `Refund · ${REFUND_METHOD_LABELS[e.method as RefundMethod] ?? "Other"}`;
    case "credit_held":
      return e.relatedInvoiceId ? "Moved to the client's credit (kept against another invoice)" : "Held as credit for the client";
    case "credit_applied":
      return "Credit applied from the client's account";
    case "adjustment":
      return ADJUSTMENT_LABELS[e.method as AdjustmentType] ?? "Adjustment";
    case "void":
      return e.method === "refund" ? "Voided · money paid refunded" : e.method === "credit" ? "Voided · money paid held as credit" : e.method === "transfer" ? "Voided · money paid kept against another invoice" : "Voided";
  }
}

/** Refunds, credits, adjustments and voids, oldest first, each with who, when and why. */
function InvoiceHistory({ invoiceId, events }: { invoiceId: string; events: InvoiceEvent[] }) {
  const check = useMutation({ mutationFn: (eventId: string) => api.send("POST", `/api/invoices/${invoiceId}/refunds/${eventId}/check`) });
  if (!events.length) return null;
  return (
    <div className="border-t border-border pt-2">
      <p className="mb-1 text-xs font-medium text-muted-foreground">Refunds, credits and adjustments</p>
      <ul className="space-y-1.5">
        {events.map((e) => (
          <li key={e.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
            <span className="min-w-0">
              <span className="font-medium">{staffEventLabel(e)}</span>
              {e.status === "pending" && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">Waiting for Stripe</span>}
              {e.status === "failed" && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-900 dark:bg-red-900/30 dark:text-red-200">Not refunded</span>}
              <span className="block text-xs text-muted-foreground">
                {formatDate(e.effectiveOn)} · {e.byName || "Staff"}
                {e.reference && ` · ${e.reference}`} · {e.reason}
                {e.showToClient && " (shown to the client)"}
              </span>
              {e.status === "pending" && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={check.isPending} onClick={() => check.mutate(e.id)}>
                  Check with Stripe
                </Button>
              )}
            </span>
            <span className={`text-right tabular ${e.status !== "succeeded" ? "text-muted-foreground line-through" : ""}`}>
              {e.kind === "void" ? (e.amountCents ? `−${formatCents(e.amountCents)}` : "—") : e.kind === "refund" || e.kind === "credit_held" ? `+${formatCents(e.amountCents)}` : `−${formatCents(e.amountCents)}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type AdjustAction = "refund" | "hold" | "apply" | "adjust" | "void";
const ACTION_LABELS: Record<AdjustAction, string> = { refund: "Refund", hold: "Hold as credit", apply: "Apply credit", adjust: "Adjust balance", void: "Void" };

/** The invoice's "Adjust" menu: each choice opens its own dialog. */
function AdjustMenu({ data }: { data: InvoiceDetail }) {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<AdjustAction | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  const inv = data.invoice;
  const live = inv.status === "open" || inv.status === "paid";
  const can: Record<AdjustAction, boolean> = {
    refund: inv.netPaidCents > 0,
    hold: inv.netPaidCents > 0,
    apply: inv.status === "open" && data.clientCreditCents > 0,
    adjust: inv.status === "open",
    void: live,
  };
  return (
    <div ref={ref} className="relative">
      <Button size="sm" variant="outline" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Adjust <ChevronDown className="ml-1 h-4 w-4" />
      </Button>
      {open && (
        <div role="menu" className="absolute left-0 z-20 mt-1 w-52 overflow-hidden rounded-md border border-border bg-card py-1 shadow-md">
          {(Object.keys(ACTION_LABELS) as AdjustAction[]).map((a) => (
            <button
              key={a}
              role="menuitem"
              disabled={!can[a]}
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => {
                setOpen(false);
                setAction(a);
              }}
            >
              {ACTION_LABELS[a]}
            </button>
          ))}
        </div>
      )}
      {action && <AdjustDialog action={action} data={data} onClose={() => setAction(null)} />}
    </div>
  );
}

function AdjustDialog({ action, data, onClose }: { action: AdjustAction; data: InvoiceDetail; onClose: () => void }) {
  const inv = data.invoice;
  const { toast } = useToast();
  const cards = data.payments.filter((p) => p.method === "stripe" && p.amountCents > (p.refundedCents ?? 0));
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<RefundMethod>(cards.length ? "stripe" : "check");
  const [paymentId, setPaymentId] = useState(cards[0]?.id ?? "");
  const [type, setType] = useState<AdjustmentType>("courtesy");
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [show, setShow] = useState(false);
  const [disposition, setDisposition] = useState<"refund" | "credit" | "transfer">("credit");
  const [target, setTarget] = useState("");
  const { data: open } = useQuery<InvoicePage>({ queryKey: ["/api/invoices?clientId=", inv.clientId, "&status=open&limit=100"], enabled: action === "void" && inv.netPaidCents > 0 });
  const targets = (open?.items ?? []).filter((i) => i.id !== inv.id);
  const card = cards.find((p) => p.id === paymentId);
  const max =
    action === "refund"
      ? method === "stripe" && card
        ? Math.min(card.amountCents - (card.refundedCents ?? 0), inv.netPaidCents)
        : inv.netPaidCents
      : action === "hold"
        ? inv.netPaidCents
        : action === "apply"
          ? Math.min(data.clientCreditCents, inv.balanceCents)
          : inv.balanceCents;
  const send = useMutation({
    meta: { inlineError: true },
    mutationFn: async () => {
      if (!reason.trim()) throw new Error("Enter the reason. It stays inside the firm unless you choose to show it to the client.");
      const base = { reason: reason.trim(), showToClient: show };
      if (action === "void") {
        const body = inv.netPaidCents > 0 ? { ...base, disposition, ...(disposition === "refund" ? { refundMethod: method, reference: reference || undefined } : {}), ...(disposition === "transfer" ? { targetInvoiceId: target } : {}) } : base;
        if (disposition === "transfer" && inv.netPaidCents > 0 && !target) throw new Error("Choose the invoice to keep the money against.");
        return api.send<InvoiceDetail & { refund?: { status: string; message: string | null } }>("POST", `/api/invoices/${inv.id}/void`, body);
      }
      let cents: number;
      try {
        cents = parseDollars(amount);
      } catch {
        throw new Error("Enter an amount such as 250 or 250.00");
      }
      if (cents <= 0) throw new Error("Enter an amount more than $0.00");
      if (action === "refund") return api.send<InvoiceDetail & { refund?: { status: string; message: string | null } }>("POST", `/api/invoices/${inv.id}/refunds`, { ...base, amountCents: cents, method, ...(method === "stripe" ? { paymentId } : {}), reference: reference || undefined });
      if (action === "hold") return api.send<InvoiceDetail>("POST", `/api/invoices/${inv.id}/credits/hold`, { ...base, amountCents: cents });
      if (action === "apply") return api.send<InvoiceDetail>("POST", `/api/invoices/${inv.id}/credits/apply`, { ...base, amountCents: cents });
      return api.send<InvoiceDetail>("POST", `/api/invoices/${inv.id}/adjustments`, { ...base, amountCents: cents, type });
    },
    onSuccess: (r) => {
      const note = (r as { refund?: { status: string; message: string | null } }).refund?.message;
      toast({ title: note ?? `${ACTION_LABELS[action]}: saved on invoice #${inv.number}` });
      onClose();
    },
  });
  const titles: Record<AdjustAction, string> = {
    refund: `Refund on invoice #${inv.number}`,
    hold: `Hold money as credit from invoice #${inv.number}`,
    apply: `Apply credit to invoice #${inv.number}`,
    adjust: `Adjust the balance of invoice #${inv.number}`,
    void: `Void invoice #${inv.number}`,
  };
  const help: Record<AdjustAction, string> = {
    refund: `Gives money back to the client and reduces the amount paid. Up to ${formatCents(Math.max(0, max))}.`,
    hold: `Moves money paid on this invoice to the client's credit, to apply to another invoice later. Up to ${formatCents(Math.max(0, max))}.`,
    apply: `Pays this invoice from the client's credit (${formatCents(data.clientCreditCents)} available). Up to ${formatCents(Math.max(0, max))}.`,
    adjust: `Takes part of the balance off: a courtesy discount, a fee waived, a settlement or a write-off. Up to ${formatCents(Math.max(0, max))}.`,
    void:
      inv.netPaidCents > 0
        ? `Cancels the invoice. ${formatCents(inv.netPaidCents)} has been paid on it: choose what happens to that money.`
        : "Cancels the invoice. The client will no longer be able to pay it.",
  };
  const select = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{titles[action]}</DialogTitle>
          <DialogDescription>{help[action]}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            send.mutate();
          }}
        >
          {action !== "void" && (
            <div className="space-y-1">
              <Label htmlFor="adj-amount">Amount</Label>
              <Input id="adj-amount" inputMode="decimal" className="tabular" placeholder={formatCents(Math.max(0, max))} value={amount} onChange={(e) => setAmount(e.target.value)} required />
            </div>
          )}
          {(action === "refund" || (action === "void" && inv.netPaidCents > 0 && disposition === "refund")) && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="adj-method">How it's refunded</Label>
                <select id="adj-method" className={select} value={method} onChange={(e) => setMethod(e.target.value as RefundMethod)}>
                  {(["stripe", "check", "transfer", "cash", "other"] as const)
                    .filter((m) => m !== "stripe" || cards.length > 0)
                    .map((m) => (
                      <option key={m} value={m}>
                        {REFUND_METHOD_LABELS[m]}
                      </option>
                    ))}
                </select>
              </div>
              {method === "stripe" && action === "refund" ? (
                <div className="space-y-1">
                  <Label htmlFor="adj-payment">Card payment</Label>
                  <select id="adj-payment" className={select} value={paymentId} onChange={(e) => setPaymentId(e.target.value)}>
                    {cards.map((p) => (
                      <option key={p.id} value={p.id}>
                        {formatDate(p.receivedOn)} · {formatCents(p.amountCents - (p.refundedCents ?? 0))} left
                      </option>
                    ))}
                  </select>
                </div>
              ) : method !== "stripe" ? (
                <div className="space-y-1">
                  <Label htmlFor="adj-ref">Reference</Label>
                  <Input id="adj-ref" placeholder="Check number or transfer ID" value={reference} onChange={(e) => setReference(e.target.value)} />
                </div>
              ) : null}
            </div>
          )}
          {action === "adjust" && (
            <div className="space-y-1">
              <Label htmlFor="adj-type">Kind of adjustment</Label>
              <select id="adj-type" className={select} value={type} onChange={(e) => setType(e.target.value as AdjustmentType)}>
                {ADJUSTMENT_TYPES.map((x) => (
                  <option key={x} value={x}>
                    {ADJUSTMENT_LABELS[x]}
                  </option>
                ))}
              </select>
            </div>
          )}
          {action === "void" && inv.netPaidCents > 0 && (
            <fieldset className="space-y-1.5">
              <legend className="mb-1 text-sm font-medium">The {formatCents(inv.netPaidCents)} paid</legend>
              {(
                [
                  ["credit", "Hold it as credit for the client"],
                  ["refund", "Refund it to the client"],
                  ["transfer", "Keep it against another open invoice"],
                ] as const
              ).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="disposition" checked={disposition === k} onChange={() => setDisposition(k)} />
                  {label}
                </label>
              ))}
              {disposition === "transfer" && (
                <select aria-label="Invoice to keep the money against" className={select} value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="">{targets.length ? "Choose an open invoice" : "This client has no other open invoice"}</option>
                  {targets.map((i) => (
                    <option key={i.id} value={i.id}>
                      #{i.number} · {formatCents(i.balanceCents ?? i.totalCents - i.amountPaidCents)} due
                    </option>
                  ))}
                </select>
              )}
              {disposition === "transfer" && <p className="text-xs text-muted-foreground">Any part that invoice doesn't need is held as credit.</p>}
            </fieldset>
          )}
          <div className="space-y-1">
            <Label htmlFor="adj-reason">Reason</Label>
            <Textarea id="adj-reason" rows={3} maxLength={2000} placeholder="Why, in a sentence. Kept inside the firm." value={reason} onChange={(e) => setReason(e.target.value)} required />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
            Show this note to the client
          </label>
          {send.error && <p className="text-sm text-destructive">{errorMessage(send.error)}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant={action === "void" ? "destructive" : "default"} disabled={send.isPending}>
              {action === "refund" ? (method === "stripe" ? "Refund through Stripe" : "Record refund") : action === "hold" ? "Hold as credit" : action === "apply" ? "Apply credit" : action === "adjust" ? "Adjust balance" : "Void invoice"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceRow({ inv, me }: { inv: InvoiceSummary; me: Me }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery<InvoiceDetail>({ queryKey: ["/api/invoices/", inv.id], enabled: open });
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("check");
  const [receivedOn, setReceivedOn] = useState(new Date().toLocaleDateString("en-CA"));
  const [reference, setReference] = useState("");
  const act = useMutation({ mutationFn: (path: string) => api.send("POST", `/api/invoices/${inv.id}/${path}`) });
  const del = useMutation({ mutationFn: () => api.send("DELETE", `/api/invoices/${inv.id}`) });
  const pay = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", `/api/invoices/${inv.id}/payments`, { amountCents: parseDollars(amount), method, receivedOn, reference: reference || undefined }),
    onSuccess: () => setAmount(""),
  });
  // The server's balance (total − adjustments − payments + refunds), never worked out here.
  const balance = data?.invoice.balanceCents ?? inv.balanceCents ?? Math.max(0, inv.totalCents - inv.amountPaidCents);
  const overpaid = data?.invoice.overpaidCents ?? inv.overpaidCents ?? 0;
  const d = data?.invoice;
  return (
    <li className="px-4 py-3">
      <button className="grid w-full grid-cols-[4.5rem_minmax(0,1fr)_auto] items-center gap-3 text-left text-sm sm:grid-cols-[4.5rem_minmax(0,1fr)_7rem_7rem_7rem_6.5rem]" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="font-medium tabular">{inv.number ? `#${inv.number}` : "Draft"}</span>
        <span className="min-w-0">
          {/* The name truncates; its badges sit under it on a phone and beside it from sm up, so a badge is never squeezed. */}
          <span className="flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
            <span className="block min-w-0 max-w-full truncate">{inv.clientName ?? (inv.issuedOn ? `Issued ${formatDate(inv.issuedOn)}` : "Not issued")}</span>
            <RefundFeeBadge inv={inv} />
            {/* Money received beyond what's owed (overpaid, or paid after a void): refund it or hold it as credit. */}
            {overpaid > 0 && <span className={`${BADGE} bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200`}>Overpaid {formatCents(overpaid)}</span>}
            {!!inv.clientCreditCents && inv.clientCreditCents > 0 && <span className={`${BADGE} bg-secondary text-muted-foreground`}>Credit {formatCents(inv.clientCreditCents)}</span>}
          </span>
          {/* On a phone the amount and balance stack under the client instead of disappearing. */}
          <span className="mt-0.5 block text-xs text-muted-foreground tabular sm:hidden">
            {inv.status === "open" ? `${formatCents(balance)} due of ${formatCents(inv.totalCents)}` : formatCents(inv.totalCents)}
            {inv.dueOn && inv.status === "open" ? ` · due ${formatDate(inv.dueOn)}` : ""}
          </span>
        </span>
        <span className="hidden text-muted-foreground tabular sm:block">{inv.dueOn ? formatDate(inv.dueOn) : "—"}</span>
        <span className="hidden text-right tabular sm:block">{formatCents(inv.totalCents)}</span>
        <span className="hidden text-right font-medium tabular sm:block">{inv.status === "open" ? formatCents(balance) : "—"}</span>
        <span className="justify-self-end">
          <InvoiceStatus status={inv.status} dueOn={inv.dueOn} />
        </span>
      </button>
      {open && data && d && (
        <div className="mt-3 space-y-3 rounded-lg bg-muted/40 p-3 text-sm">
          <table className="w-full">
            <tbody>
              {data.lines.map((l) => (
                <tr key={l.id}>
                  <td className="py-0.5">{l.description}</td>
                  <td className="text-right text-muted-foreground tabular">
                    {l.quantity} × {formatCents(l.unitPriceCents)}
                  </td>
                  <td className="w-28 text-right tabular">{formatCents(l.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.payments.length > 0 && (
            <ul className="space-y-0.5 border-t border-border pt-2">
              {data.payments.map((p) => (
                <li key={p.id} className="flex justify-between gap-3">
                  <span className="min-w-0">
                    {METHOD_LABELS[p.method] ?? p.method} · {formatDate(p.receivedOn)}
                    {p.reference && ` · ${p.reference}`}
                    {!!p.refundedCents && <span className="text-muted-foreground"> · {formatCents(p.refundedCents)} refunded by card</span>}
                  </span>
                  <span className="tabular">−{formatCents(p.amountCents)}</span>
                </li>
              ))}
            </ul>
          )}
          <InvoiceHistory invoiceId={inv.id} events={data.events ?? []} />
          {inv.approvalDeclinedAt && inv.approvalDeclineNote && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-900 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
              The client didn't approve this fee ({formatDate(inv.approvalDeclinedAt.slice(0, 10))}): "{inv.approvalDeclineNote}". Change the invoice, then send it for approval again.
            </p>
          )}
          {inv.clientApprovedAt && <p className="text-xs text-muted-foreground">Approved by the client on {formatDate(inv.clientApprovedAt.slice(0, 10))}, to be paid from the refund. Record the refund partner's payment as "From the refund".</p>}
          {/* The totals tie: total − adjustments − payments + refunds = balance due. */}
          <dl className="space-y-0.5 border-t border-border pt-2">
            {(d.adjustedCents > 0 || d.refundedCents > 0 || d.creditedCents > 0) && (
              <>
                <div className="flex justify-between text-muted-foreground">
                  <dt>Invoice total</dt>
                  <dd className="tabular">{formatCents(d.totalCents)}</dd>
                </div>
                {d.adjustedCents > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <dt>{d.status === "void" ? "Adjustments and void" : "Adjustments"}</dt>
                    <dd className="tabular">−{formatCents(d.adjustedCents)}</dd>
                  </div>
                )}
                <div className="flex justify-between text-muted-foreground">
                  <dt>Payments</dt>
                  <dd className="tabular">−{formatCents(d.amountPaidCents)}</dd>
                </div>
                {d.refundedCents + d.creditedCents > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <dt>Refunds and money moved to credit</dt>
                    <dd className="tabular">+{formatCents(d.refundedCents + d.creditedCents)}</dd>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-between font-medium">
              <dt>Balance due</dt>
              <dd className="tabular">{formatCents(balance)}</dd>
            </div>
            {overpaid > 0 && (
              <div className="flex justify-between text-red-700 dark:text-red-300">
                <dt>Overpaid: refund it or hold it as credit</dt>
                <dd className="tabular">{formatCents(overpaid)}</dd>
              </div>
            )}
            {data.clientCreditCents > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <dt>Client's credit with the firm</dt>
                <dd className="tabular">{formatCents(data.clientCreditCents)}</dd>
              </div>
            )}
          </dl>
          <div className="flex flex-wrap gap-2">
            {inv.status === "draft" && hasPermission(me, "billing.write") && (
              <>
                {!inv.paidFromRefund && (
                  <Button size="sm" onClick={() => act.mutate("issue")}>
                    Issue
                  </Button>
                )}
                {inv.approvalRequestedAt && !inv.approvalDeclinedAt ? (
                  <Button size="sm" variant="outline" onClick={() => act.mutate("withdraw-approval")}>
                    Withdraw approval request
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () =>
                      (await ask({
                        title: inv.approvalDeclinedAt ? "Send the changed fee for approval again?" : "Send this fee for the client's approval?",
                        description: "The fee will be taken from the client's refund by your refund-transfer partner. The client approves it in their portal, which issues the invoice; they are never asked to pay it online.",
                        confirmLabel: "Send for approval",
                      })) && act.mutate("request-approval")
                    }
                  >
                    {inv.approvalDeclinedAt ? "Send for approval again" : "Pay from refund: ask client to approve"}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={async () => (await ask({ title: "Delete this draft invoice?", confirmLabel: "Delete draft", destructive: true })) && del.mutate()}>
                  Delete draft
                </Button>
              </>
            )}
            {inv.status !== "draft" && data.canAdjust && <AdjustMenu data={data} />}
          </div>
          {inv.status === "open" && hasPermission(me, "billing.manage") && (
            <form
              className="grid gap-2 border-t border-border pt-3 sm:grid-cols-[1fr_1.4fr_1fr_1fr_auto]"
              onSubmit={(e) => {
                e.preventDefault();
                pay.mutate();
              }}
            >
              <Input aria-label="Amount received" inputMode="decimal" placeholder={formatCents(balance)} value={amount} onChange={(e) => setAmount(e.target.value)} required />
              <select aria-label="Payment method" value={method} onChange={(e) => setMethod(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                {["check", "cash", "zelle", "bank_product", "other"].map((m) => (
                  <option key={m} value={m}>
                    {METHOD_LABELS[m]}
                  </option>
                ))}
              </select>
              <Input aria-label="Date received" type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} required />
              <Input aria-label="Reference" placeholder="Check #" value={reference} onChange={(e) => setReference(e.target.value)} />
              <Button type="submit" size="sm" disabled={pay.isPending}>
                Record payment
              </Button>
              {pay.error && <p className="text-sm text-destructive sm:col-span-5">{errorMessage(pay.error)}</p>}
            </form>
          )}
        </div>
      )}
    </li>
  );
}

export function InvoiceList({ invoices, me, empty = "No invoices.", paged }: { invoices: InvoiceSummary[]; me: Me; empty?: string; paged?: Paged }) {
  if (!invoices.length) return <Empty>{empty}</Empty>;
  const showClient = invoices.some((i) => i.clientName);
  return (
    <div className="overflow-hidden rounded-lg border border-card-border bg-card">
      <div className="hidden grid-cols-[4.5rem_minmax(0,1fr)_7rem_7rem_7rem_6.5rem] gap-3 border-b border-border bg-muted/50 px-4 py-2 text-xs font-medium text-muted-foreground sm:grid">
        <span>Invoice</span>
        <span>{showClient ? "Client" : "Issued"}</span>
        <span>Due date</span>
        <span className="text-right">Amount</span>
        <span className="text-right">Balance due</span>
        <span className="text-right">Status</span>
      </div>
      <ul className="divide-y divide-border">
        {invoices.map((i) => (
          <InvoiceRow key={i.id} inv={i} me={me} />
        ))}
      </ul>
      {paged && <Pager paged={paged} noun="invoices" className="px-4 pb-3" />}
    </div>
  );
}

export function InvoicesPanel({ clientId, me, startNew = false }: { clientId: string; me: Me; startNew?: boolean }) {
  const { offset, size, paged } = useServerPaged(clientId);
  const { data, isLoading } = useQuery<InvoicePage>({ queryKey: ["/api/invoices?clientId=", clientId, `&offset=${offset}&limit=${size}`], placeholderData: (prev) => prev });
  // The balance covers every open invoice, not just the page shown.
  const { data: aging } = useQuery<Aging>({ queryKey: ["/api/invoices/aging?clientId=", clientId] });
  const [adding, setAdding] = useState(startNew && hasPermission(me, "billing.write"));
  const openCount = aging?.openCount ?? 0;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {openCount ? `Balance due: ${formatCents(aging!.balanceCents)} on ${openCount} open invoice${openCount > 1 ? "s" : ""}` : "No balance due."}
          {!!aging?.creditCents && <span className="ml-1 font-medium text-foreground">Credit held for this client: {formatCents(aging.creditCents)}.</span>}
        </p>
        {hasPermission(me, "billing.write") && !adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="mr-1 h-4 w-4" /> New invoice
          </Button>
        )}
      </div>
      {adding && <NewInvoice clientId={clientId} onDone={() => setAdding(false)} />}
      {isLoading ? <Loading size="box" /> : <InvoiceList invoices={data?.items ?? []} me={me} paged={paged(data?.total ?? 0)} empty="No invoices have been issued to this client." />}
    </div>
  );
}

/** "Release upon payment" control for one document. */
export function LockControl({ doc, clientId }: { doc: { id: string; lockedByInvoiceId?: string | null }; clientId: string }) {
  // Only an open invoice can hold a document, so the client's open invoices are all this needs.
  const { data } = useQuery<InvoicePage>({ queryKey: ["/api/invoices?clientId=", clientId, "&status=open&limit=1000"] });
  const lock = useMutation({ mutationFn: (invoiceId: string | null) => api.send("POST", `/api/documents/${doc.id}/lock`, { invoiceId }) });
  const open = data?.items ?? [];
  const held = open.some((i) => i.id === doc.lockedByInvoiceId);
  if (!open.length && !held) return null;
  return (
    <select
      aria-label="Release upon payment"
      title="Withhold this document from the client until an invoice is paid"
      value={held ? doc.lockedByInvoiceId! : ""}
      onChange={(e) => lock.mutate(e.target.value || null)}
      className="h-8 max-w-44 rounded-md border border-input bg-background px-2 text-xs"
    >
      <option value="">Released to client</option>
      {open.map((i) => (
        <option key={i.id} value={i.id}>
          Release upon payment of #{i.number}
        </option>
      ))}
    </select>
  );
}

type Member = { userId: string; name: string; email: string; role: string; isActive: boolean; pending: boolean };

/**
 * Firm Settings: who besides owners and admins may refund, credit, adjust and void invoices.
 * Only the owner changes it; everyone with billing access can see who is named.
 */
export function BillingAdjustersSettings({ canEdit }: { canEdit: boolean }) {
  const { toast } = useToast();
  const { data: settings } = useQuery<{ billingAdjusters?: string[] }>({ queryKey: ["/api/firm/settings"] });
  const { data: members } = useQuery<Member[]>({ queryKey: ["/api/firm/members"] });
  const [picked, setPicked] = useState<string[] | null>(null);
  useEffect(() => {
    if (settings && picked === null) setPicked(settings.billingAdjusters ?? []);
  }, [settings, picked]);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: (ids: string[]) => api.send("PATCH", "/api/firm/settings", { billingAdjusters: ids }),
    onSuccess: () => toast({ title: "Settings saved" }),
  });
  const staff = (members ?? []).filter((m) => m.role === "staff" && m.isActive && !m.pending);
  return (
    <Section
      title="Refunds, credits and adjustments"
      description="Owners and admins can refund, hold or apply credit, adjust and void any invoice. Name other staff here to let them do the same; every change records its reason and who made it."
    >
      {picked === null || !members ? (
        <LoadingBox label="Loading who can adjust…" className="min-h-[7.75rem] sm:min-h-16" />
      ) : staff.length === 0 ? (
        <p className="text-sm text-muted-foreground">No staff members yet. Staff you add under Team and access can be named here.</p>
      ) : (
        <div className="space-y-3">
          <ul className="grid gap-2 sm:grid-cols-2">
            {staff.map((m) => (
              <li key={m.userId} className="min-w-0">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    disabled={!canEdit}
                    checked={picked.includes(m.userId)}
                    onChange={(e) => setPicked(e.target.checked ? [...picked, m.userId] : picked.filter((x) => x !== m.userId))}
                  />
                  <span className="truncate">{m.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{m.email}</span>
                </label>
              </li>
            ))}
          </ul>
          {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
          {canEdit ? (
            <div className="flex justify-end">
              <Button size="sm" disabled={save.isPending} onClick={() => save.mutate(picked)}>
                Save who can adjust
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Only the owner can change who is named.</p>
          )}
        </div>
      )}
    </Section>
  );
}

/** The client's view of a refund, credit or adjustment: a plain line, the note only if staff chose to show it. */
export function PortalInvoiceEventRows({ events }: { events: ClientInvoiceEvent[] }) {
  return (
    <>
      {events
        .filter((e) => e.kind !== "void" || e.amountCents > 0)
        .map((e) => (
          <tr key={`${e.kind}-${e.effectiveOn}-${e.amountCents}-${e.note ?? ""}`} className="text-muted-foreground">
            <td className="py-1">
              {clientEventLabel(e)}
              {e.note && <span className="block text-xs">{e.note}</span>}
            </td>
            <td className="whitespace-nowrap py-1 pl-3 text-right align-top tabular">{e.kind === "refund" || e.kind === "credit_held" ? "+" : "−"}{formatCents(e.amountCents)}</td>
          </tr>
        ))}
    </>
  );
}

/** "You have a credit of $100.00 with your firm", wherever the client's invoices are. */
export function PortalCreditNote({ clientId }: { clientId?: string }) {
  const { data } = useQuery<Array<{ clientId: string; clientName: string; creditCents: number }>>({ queryKey: ["/api/portal/credits"] });
  const rows = (data ?? []).filter((c) => c.creditCents > 0 && (!clientId || c.clientId === clientId));
  if (!rows.length) return null;
  const many = (data ?? []).length > 1;
  return (
    <div className="space-y-1">
      {rows.map((c) => (
        <p key={c.clientId} className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200">
          {many ? `${c.clientName} has` : "You have"} a credit of <span className="font-semibold tabular">{formatCents(c.creditCents)}</span> with your firm. It will be applied to a future invoice.
        </p>
      ))}
    </div>
  );
}
